import React, { Suspense, lazy } from 'react';
import FlowerLoader from '../UI/FlowerLoader';

/**
 * 🚀 КОМПОНЕНТ ДЛЯ ЛЕНИВОЙ ЗАГРУЗКИ
 * - Code splitting для больших компонентов
 * - Красивый loader
 * - Обработка ошибок
 */

// Единый загрузчик на базе flower2.svg
const SkeletonLoader = ({ type = 'page' }) => {
  if (type === 'page') {
    return <FlowerLoader fullScreen />;
  }

  if (type === 'card') {
    return (
      <div className="flex items-center justify-center py-10">
        <FlowerLoader size={56} />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-32">
      <FlowerLoader size={40} />
    </div>
  );
};

// Компонент для обработки ошибок ленивой загрузки
class LazyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Lazy loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-gray-500">
          <svg className="w-8 h-8 mb-2" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <p className="text-sm">Что-то пошло не так. Попробуйте обновить страницу.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC для создания ленивых компонентов с улучшенной загрузкой
export const createLazyComponent = (importFunc, fallbackType = 'page') => {
  const LazyComponent = lazy(importFunc);
  
  return React.forwardRef((props, ref) => (
    <LazyErrorBoundary>
      <Suspense fallback={<SkeletonLoader type={fallbackType} />}>
        <LazyComponent {...props} ref={ref} />
      </Suspense>
    </LazyErrorBoundary>
  ));
};

// Предзагрузка критических компонентов
export const preloadComponent = (importFunc) => {
  const componentImport = importFunc();
  if (componentImport && typeof componentImport.then === 'function') {
    componentImport.catch(() => {
      // Игнорируем ошибки предзагрузки
    });
  }
  return componentImport;
};

// Хук для предзагрузки на hover
export const usePreloadOnHover = (importFunc) => {
  const preload = React.useCallback(() => {
    preloadComponent(importFunc);
  }, [importFunc]);

  return preload;
};

export { SkeletonLoader, LazyErrorBoundary };
